<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div v-if="loading" class="spinner" aria-label="Lädt"></div>

      <div v-else-if="notFound" class="empty-state">
        Masterartikel nicht gefunden.
      </div>

      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <template v-else-if="article">
        <div class="dashboard-header">
          <h1 class="dashboard-title">{{ article.description }}</h1>
          <p class="dashboard-subtitle">Master-Artikel #{{ article.id }}</p>
        </div>

        <section class="warranty-section">
          <h2 class="warranty-section-title">Garantie</h2>

          <div v-if="warrantyError" class="error">{{ warrantyError }}</div>

          <div v-else class="warranty-fields">
            <div class="warranty-field">
              <label class="form-label" for="warranty-sp">Servicepartner</label>
              <div class="warranty-select-row">
                <select
                  id="warranty-sp"
                  v-model="warrantySelectionRaw"
                  class="form-select"
                  :disabled="warrantySaving || servicepartnersLoading"
                  @change="onWarrantyChange"
                >
                  <option :value="''">— Kein Servicepartner —</option>
                  <option
                    v-for="sp in activeServicepartners"
                    :key="sp.id"
                    :value="String(sp.id)"
                  >{{ sp.description }}</option>
                  <option
                    v-if="orphanServicepartner"
                    :value="String(orphanServicepartner.id)"
                    disabled
                  >{{ orphanServicepartner.description }} (deaktiviert)</option>
                </select>
                <span v-if="warrantySaving" class="warranty-saving">Speichert…</span>
              </div>
            </div>

            <div class="warranty-field">
              <label class="form-label" for="warranty-months">Garantie (Monate)</label>
              <div class="warranty-select-row">
                <input
                  id="warranty-months"
                  v-model="warrantyMonthsRaw"
                  type="number"
                  min="1"
                  step="1"
                  class="form-control warranty-months-input"
                  :disabled="warrantyMonthsSaving"
                  @blur="onWarrantyMonthsBlur"
                />
                <span v-if="warrantyMonthsSaving" class="warranty-saving">Speichert…</span>
              </div>
            </div>
          </div>

          <div v-if="warrantySaveError" class="error warranty-save-error">{{ warrantySaveError }}</div>
          <div v-if="warrantyMonthsSaveError" class="error warranty-save-error">{{ warrantyMonthsSaveError }}</div>
        </section>

        <section class="recipes-section">
          <div class="recipes-section-header">
            <h2 class="recipes-section-title">Fehlerbilder &amp; Ersatzteile</h2>

            <button
              type="button"
              class="btn btn-primary"
              :disabled="adding || servicepartnersLoading"
              @click="openAddModal"
            >+ Fehlerbild hinzufügen</button>
          </div>

          <div v-if="recipesLoading" class="spinner" aria-label="Lädt"></div>
          <div v-else-if="recipesError" class="error">{{ recipesError }}</div>

          <div v-else-if="recipes.length === 0" class="empty-state">
            Noch keine Fehlerbilder hinterlegt. Lege oben eines für einen Servicepartner an.
          </div>

          <div v-else class="recipes-list">
            <section v-for="grp in recipesByPartner" :key="grp.id" class="recipes-partner">
              <h3 class="recipes-partner-title">{{ grp.description }}</h3>

              <article v-for="r in grp.recipes" :key="r.id" class="recipe-card">
                <header class="recipe-card-header">
                  <h4 class="recipe-card-title">
                    {{ r.error_pattern.description }}
                    <!-- Sonst sucht niemand, warum ein Fehlerbild auswählbar ist,
                         das an diesem Master nicht angelegt wurde. -->
                    <span v-if="r.is_global" class="recipe-global-badge">gilt für alle Master</span>
                  </h4>

                  <div class="recipe-surcharge">
                    <label :for="`surcharge-${r.id}`">Aufschlag</label>
                    <input
                      :id="`surcharge-${r.id}`"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control"
                      :value="r.surcharge_net_eur"
                      @input="onSurchargeInput(r, ($event.target as HTMLInputElement).value)"
                    />
                    <span class="recipe-surcharge-suffix">€ netto</span>
                  </div>

                  <button
                    type="button"
                    class="btn btn-icon btn-danger"
                    title="Fehlerbild entfernen"
                    @click="removeRecipe(r)"
                  >✕</button>
                </header>

                <p v-if="r.is_global" class="recipe-global-hint">
                  Diese Rezeptur hängt an keinem Masterartikel. Änderungen an Aufschlag,
                  Gruppen und Artikeln wirken für jedes Gerät.
                </p>

                <div class="recipe-groups">
                  <div v-for="g in r.groups" :key="g.id" class="recipe-group">
                    <div class="recipe-group-header">
                      <span class="recipe-group-label-tag">{{ g.label || 'Gruppe' }}</span>
                      <span class="recipe-group-hint">Einer dieser Artikel reicht</span>
                      <button
                        type="button"
                        class="btn btn-icon btn-danger"
                        title="Gruppe entfernen"
                        @click="removeGroup(r, g)"
                      >✕</button>
                    </div>

                    <ul v-if="g.items.length > 0" class="recipe-items">
                      <li v-for="it in g.items" :key="it.id" class="recipe-item">
                        <span class="recipe-item-id">#{{ it.device_id }}</span>
                        <span class="recipe-item-desc">{{ it.device_description || '—' }}</span>
                        <span v-if="it.ek_price != null" class="recipe-item-price">{{ formatPrice(it.ek_price) }}</span>
                        <button
                          type="button"
                          class="btn btn-icon btn-danger"
                          title="Artikel entfernen"
                          @click="removeItem(r, g, it)"
                        >✕</button>
                      </li>
                    </ul>
                    <div v-else class="recipe-items-empty">Noch kein Artikel ausgewählt.</div>

                    <DeviceSearchInput
                      placeholder="Ersatzteil suchen (Bezeichnung, ID, Barcode)…"
                      :exclude-ids="g.items.map(i => i.device_id)"
                      :type-id="SPARE_PART_TYPE_ID"
                      @update:selected="addItem(r, g, $event)"
                    />
                  </div>

                  <button type="button" class="btn btn-outline-primary" @click="addGroup(r)">
                    + Gruppe hinzufügen
                  </button>
                </div>
              </article>
            </section>
          </div>
        </section>
      </template>
    </main>

    <!-- Bootstrap-Modal: Fehlerbild für einen Servicepartner anlegen -->
    <div
      ref="addModalEl"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="addRecipeTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="addRecipe" novalidate>
            <div class="modal-header">
              <h5 class="modal-title" id="addRecipeTitle">Fehlerbild hinzufügen</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
            </div>

            <div class="modal-body">
              <div v-if="addError" class="alert alert-danger" role="alert">{{ addError }}</div>

              <div class="mb-3">
                <label for="add-recipe-sp" class="form-label">Servicepartner</label>
                <select
                  id="add-recipe-sp"
                  v-model="newServicepartnerId"
                  class="form-select"
                  :disabled="activeServicepartners.length === 0 || adding"
                >
                  <option :value="null" disabled>— Bitte wählen —</option>
                  <option v-for="sp in activeServicepartners" :key="sp.id" :value="sp.id">
                    {{ sp.description }}
                  </option>
                </select>
              </div>

              <div class="mb-1">
                <label for="add-recipe-pattern" class="form-label">Fehlerbild</label>
                <select
                  id="add-recipe-pattern"
                  v-model="newPatternId"
                  class="form-select"
                  :disabled="availablePatterns.length === 0 || adding"
                >
                  <option :value="null" disabled>— Bitte wählen —</option>
                  <option v-for="p in availablePatterns" :key="p.id" :value="p.id">{{ p.description }}</option>
                </select>
                <div v-if="newServicepartnerId !== null && availablePatterns.length === 0" class="form-text">
                  Für diesen Servicepartner sind bereits alle Fehlerbilder angelegt.
                </div>
              </div>

              <!-- Ohne Master: gilt für jedes Gerät, auch für künftig angelegte
                   Masterartikel. Für Regeln, die nicht am Gerät hängen (Test,
                   Löschen, Reinigung). -->
              <div class="form-check mt-3">
                <input
                  id="add-recipe-global"
                  v-model="newIsGlobal"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="adding"
                />
                <label class="form-check-label" for="add-recipe-global">
                  für alle Masterartikel — nicht nur für diesen
                </label>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="adding">
                Abbrechen
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="adding || newServicepartnerId === null || newPatternId === null"
              >
                {{ adding ? 'Legt an …' : 'Anlegen' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import TopNav from '../components/TopNav.vue';
import DeviceSearchInput from '../components/DeviceSearchInput.vue';
import {
  get,
  setWarrantyServicepartner,
  setWarrantyMonths,
  ApiError,
  type MasterArticleDetail,
  type WarrantyServicepartner,
} from '../services/masterarticles';
import {
  list as listServicepartners,
  type Servicepartner,
} from '../services/servicepartner';
import {
  listErrorPatterns,
  listRecipes,
  createRecipe,
  patchRecipe,
  deleteRecipe,
  createGroup,
  deleteGroup,
  createItem,
  deleteItem,
  ApiError as RecipeApiError,
  type ErrorPattern,
  type RepairRecipe,
  type RepairRecipeGroup,
  type RepairRecipeItem,
  type DeviceSearchResult,
} from '../services/repairRecipes';

const props = defineProps<{ id: number }>();

// handyb_shop.device_types.id = 14 → "Ersatzteil"
const SPARE_PART_TYPE_ID = 14;

const article    = ref<MasterArticleDetail | null>(null);
const loading    = ref(true);
const loadError  = ref<string | null>(null);
const notFound   = ref(false);

const recipes        = ref<RepairRecipe[]>([]);
const errorPatterns  = ref<ErrorPattern[]>([]);
const recipesLoading = ref(true);
const recipesError   = ref<string | null>(null);
const newPatternId   = ref<number | null>(null);
// Ohne Master anlegen: die Rezeptur gilt dann für jeden Masterartikel.
const newIsGlobal    = ref(false);
const adding         = ref(false);

const newServicepartnerId = ref<number | null>(null);
const addError            = ref<string | null>(null);
const addModalEl          = useTemplateRef<HTMLDivElement>('addModalEl');
let   addModal: Modal | null = null;

const surchargeDebounce = new Map<number, number>();

const servicepartners        = ref<Servicepartner[]>([]);
const servicepartnersLoading = ref(true);
const warrantyError          = ref<string | null>(null);
const warrantySelectionRaw   = ref<string>('');
const warrantySaving         = ref(false);
const warrantySaveError      = ref<string | null>(null);

const warrantyMonthsRaw       = ref<string>('');
const warrantyMonthsSaving    = ref(false);
const warrantyMonthsSaveError = ref<string | null>(null);

const activeServicepartners = computed(() =>
  [...servicepartners.value].sort((a, b) => a.description.localeCompare(b.description, 'de')),
);

const orphanServicepartner = computed<WarrantyServicepartner | null>(() => {
  const current = article.value?.warranty_servicepartner;
  if (!current) return null;
  if (servicepartners.value.some(sp => sp.id === current.id)) return null;
  return current;
});

/** Rezepturen nach Servicepartner gebündelt, Partner alphabetisch. */
const recipesByPartner = computed(() => {
  const groups = new Map<number, { id: number; description: string; recipes: RepairRecipe[] }>();
  for (const r of recipes.value) {
    let group = groups.get(r.servicepartner.id);
    if (group === undefined) {
      group = { id: r.servicepartner.id, description: r.servicepartner.description, recipes: [] };
      groups.set(r.servicepartner.id, group);
    }
    group.recipes.push(r);
  }
  return [...groups.values()].sort((a, b) => a.description.localeCompare(b.description, 'de'));
});

// Ein Fehlerbild darf pro Servicepartner nur einmal vorkommen — belegt sind
// immer nur die des im Modal gewählten Partners.
//
// Beim Anlegen einer globalen Rezeptur zählen nur die globalen als belegt, beim
// Anlegen für diesen Master nur seine eigenen: eine Master-eigene Rezeptur neben
// einer globalen ist erlaubt, genau so entsteht die Abweichung.
const availablePatterns = computed(() => {
  const partnerId = newServicepartnerId.value;
  const used = new Set(
    recipes.value
      .filter(r => r.servicepartner.id === partnerId && r.is_global === newIsGlobal.value)
      .map(r => r.error_pattern.id),
  );
  return errorPatterns.value.filter(p => !used.has(p.id));
});

onMounted(async () => {
  if (addModalEl.value) {
    addModal = new Modal(addModalEl.value);
  }

  const [detailResult, servicepartnersResult] = await Promise.allSettled([
    get(props.id),
    listServicepartners(),
  ]);

  if (detailResult.status === 'fulfilled') {
    article.value             = detailResult.value;
    warrantySelectionRaw.value = detailResult.value.warranty_servicepartner
      ? String(detailResult.value.warranty_servicepartner.id)
      : '';
    warrantyMonthsRaw.value = detailResult.value.warranty_in_months !== null
      ? String(detailResult.value.warranty_in_months)
      : '';
  } else {
    const e = detailResult.reason;
    if (e instanceof ApiError && e.code === 'NOT_FOUND') {
      notFound.value = true;
    } else {
      loadError.value = e instanceof Error ? e.message : 'Unbekannter Fehler.';
    }
    loading.value = false;
    return;
  }
  loading.value = false;

  if (servicepartnersResult.status === 'fulfilled') {
    servicepartners.value = servicepartnersResult.value;
  } else {
    const e = servicepartnersResult.reason;
    warrantyError.value = e instanceof Error ? e.message : 'Servicepartner konnten nicht geladen werden.';
  }
  servicepartnersLoading.value = false;

  try {
    const [r, p] = await Promise.all([listRecipes(props.id), listErrorPatterns()]);
    recipes.value       = r;
    errorPatterns.value = p;
  } catch (e) {
    recipesError.value = e instanceof Error ? e.message : 'Unbekannter Fehler.';
  } finally {
    recipesLoading.value = false;
  }
});

async function onWarrantyChange() {
  if (!article.value) return;
  const previousSelection = article.value.warranty_servicepartner
    ? String(article.value.warranty_servicepartner.id)
    : '';
  const newRaw = warrantySelectionRaw.value;
  const newId  = newRaw === '' ? null : Number.parseInt(newRaw, 10);

  warrantySaving.value    = true;
  warrantySaveError.value = null;
  try {
    const persisted = await setWarrantyServicepartner(props.id, newId);
    article.value.warranty_servicepartner = persisted;
  } catch (e) {
    warrantySelectionRaw.value = previousSelection;
    warrantySaveError.value    = e instanceof Error
      ? `Speichern fehlgeschlagen: ${e.message}`
      : 'Speichern fehlgeschlagen.';
  } finally {
    warrantySaving.value = false;
  }
}

async function onWarrantyMonthsBlur() {
  if (!article.value) return;

  const previousValue = article.value.warranty_in_months;
  const previousRaw   = previousValue !== null ? String(previousValue) : '';
  const rawAsString   = warrantyMonthsRaw.value == null ? '' : String(warrantyMonthsRaw.value);
  const trimmed       = rawAsString.trim();

  let newMonths: number | null;
  if (trimmed === '') {
    newMonths = null;
  } else {
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || String(parsed) !== trimmed) {
      warrantyMonthsRaw.value       = previousRaw;
      warrantyMonthsSaveError.value = 'Bitte eine positive Ganzzahl eingeben (oder leer für „keine Garantie").';
      return;
    }
    newMonths = parsed;
  }

  if (newMonths === previousValue) {
    warrantyMonthsRaw.value       = newMonths !== null ? String(newMonths) : '';
    warrantyMonthsSaveError.value = null;
    return;
  }

  warrantyMonthsSaving.value    = true;
  warrantyMonthsSaveError.value = null;
  try {
    const persisted = await setWarrantyMonths(props.id, newMonths);
    article.value.warranty_in_months = persisted;
    warrantyMonthsRaw.value          = persisted !== null ? String(persisted) : '';
  } catch (e) {
    warrantyMonthsRaw.value       = previousRaw;
    warrantyMonthsSaveError.value = e instanceof Error
      ? `Speichern fehlgeschlagen: ${e.message}`
      : 'Speichern fehlgeschlagen.';
  } finally {
    warrantyMonthsSaving.value = false;
  }
}

/**
 * Vorbelegung: der Garantie-Servicepartner des Masters, sonst der erste aktive —
 * so muss im Normalfall nur noch das Fehlerbild gewählt werden.
 */
function openAddModal() {
  const warrantyId = article.value?.warranty_servicepartner?.id ?? null;
  const preselect  = activeServicepartners.value.find(sp => sp.id === warrantyId)
    ?? activeServicepartners.value[0]
    ?? null;

  newServicepartnerId.value = preselect ? preselect.id : null;
  newPatternId.value        = null;
  newIsGlobal.value         = false;
  addError.value            = null;
  addModal?.show();
}

async function addRecipe() {
  if (newServicepartnerId.value === null || newPatternId.value === null) return;
  adding.value   = true;
  addError.value = null;
  try {
    const created = await createRecipe(props.id, {
      servicepartner_location_id: newServicepartnerId.value,
      error_pattern_id:           newPatternId.value,
      is_global:                  newIsGlobal.value,
    });
    recipes.value = [...recipes.value, created].sort(
      (a, b) => a.servicepartner.description.localeCompare(b.servicepartner.description, 'de')
             || a.error_pattern.id - b.error_pattern.id,
    );
    newPatternId.value = null;
    addModal?.hide();
  } catch (e) {
    addError.value = formatError(e, 'Fehlerbild konnte nicht angelegt werden.');
  } finally {
    adding.value = false;
  }
}

async function removeRecipe(r: RepairRecipe) {
  const scope = r.is_global
    ? 'Diese Rezeptur gilt für ALLE Masterartikel und verschwindet damit überall.'
    : '';
  if (!confirm(`Fehlerbild „${r.error_pattern.description}" für ${r.servicepartner.description} wirklich entfernen?`
    + (scope !== '' ? `\n\n${scope}` : ''))) return;
  try {
    await deleteRecipe(props.id, r.id);
    recipes.value = recipes.value.filter(x => x.id !== r.id);
  } catch (e) {
    alert(formatError(e, 'Konnte nicht gelöscht werden.'));
  }
}

function onSurchargeInput(r: RepairRecipe, raw: string) {
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value) || value < 0) return;
  r.surcharge_net_eur = value;

  const existing = surchargeDebounce.get(r.id);
  if (existing !== undefined) window.clearTimeout(existing);
  const handle = window.setTimeout(async () => {
    try {
      await patchRecipe(props.id, r.id, { surcharge_net_eur: value });
    } catch (e) {
      alert(formatError(e, 'Aufschlag konnte nicht gespeichert werden.'));
    }
  }, 500);
  surchargeDebounce.set(r.id, handle);
}

async function addGroup(r: RepairRecipe) {
  try {
    const group = await createGroup(props.id, r.id);
    r.groups = [...r.groups, group];
  } catch (e) {
    alert(formatError(e, 'Gruppe konnte nicht angelegt werden.'));
  }
}

async function removeGroup(r: RepairRecipe, g: RepairRecipeGroup) {
  if (!confirm('Gruppe wirklich entfernen?')) return;
  try {
    await deleteGroup(props.id, r.id, g.id);
    r.groups = r.groups.filter(x => x.id !== g.id);
  } catch (e) {
    alert(formatError(e, 'Gruppe konnte nicht gelöscht werden.'));
  }
}

async function addItem(r: RepairRecipe, g: RepairRecipeGroup, d: DeviceSearchResult) {
  try {
    const created = await createItem(props.id, r.id, g.id, { device_id: d.id });
    g.items = [...g.items, created];
  } catch (e) {
    alert(formatError(e, 'Artikel konnte nicht hinzugefügt werden.'));
  }
}

async function removeItem(r: RepairRecipe, g: RepairRecipeGroup, it: RepairRecipeItem) {
  try {
    await deleteItem(props.id, r.id, g.id, it.id);
    g.items = g.items.filter(x => x.id !== it.id);
  } catch (e) {
    alert(formatError(e, 'Artikel konnte nicht entfernt werden.'));
  }
}

function formatError(e: unknown, fallback: string): string {
  if (e instanceof RecipeApiError) {
    if (e.code === 'CONFLICT' && e.message === 'already_in_group') return 'Artikel ist bereits in dieser Gruppe.';
    if (e.code === 'CONFLICT' && e.message === 'already_exists')   return 'Dieses Fehlerbild ist schon angelegt.';
    if (e.code === 'BAD_REQUEST' && e.message === 'device_is_master') return 'Master-Artikel können nicht als Ersatzteil gewählt werden.';
    return `${fallback} (${e.message})`;
  }
  return fallback;
}

function formatPrice(value: number): string {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(value);
}
</script>

<style scoped>
.warranty-section {
  margin-top: 24px;
  padding: 16px 18px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #d4d4d8);
  border-radius: 10px;
}

.warranty-section-title {
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 600;
}

.warranty-fields {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.warranty-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.warranty-select-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.warranty-select-row .form-select {
  max-width: 360px;
}

.warranty-months-input {
  width: 140px;
}

.warranty-saving {
  color: #6c757d;
  font-size: 13px;
}

.warranty-save-error {
  margin-top: 8px;
}

/* Kennzeichen an einer Rezeptur ohne Master — sie steht in jeder Masterliste. */
.recipe-global-badge {
  margin-left: 8px;
  padding: 1px 7px;
  border: 1px solid var(--accent, #f7a600);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--accent-hover, #e09600);
  vertical-align: middle;
  white-space: nowrap;
}

.recipe-global-hint {
  margin: 0 0 10px;
  font-size: 0.85rem;
  color: var(--muted, #6c757d);
}

.recipes-section {
  margin-top: 24px;
}

.recipes-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.recipes-section-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.recipes-list {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* Ein Block je Servicepartner — die Rezepturen darunter gehören alle ihm. */
.recipes-partner {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recipes-partner-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--text-muted, #6c757d);
  border-bottom: 1px solid var(--border, #d4d4d8);
  padding-bottom: 6px;
}

.recipe-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #d4d4d8);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 18px 20px;
}

.recipe-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
  margin-bottom: 14px;
}

.recipe-card-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  flex: 1;
  min-width: 200px;
}

.recipe-surcharge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.recipe-surcharge label {
  margin: 0;
  color: #6c757d;
}

.recipe-surcharge .form-control {
  width: 100px;
  text-align: right;
}

.recipe-surcharge-suffix {
  color: #6c757d;
}

.recipe-groups {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.recipe-group {
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 12px 14px;
}

.recipe-group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.recipe-group-label-tag {
  background: #f7a600;
  color: #fff;
  border-radius: 999px;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.recipe-group-hint {
  color: #6c757d;
  font-size: 13px;
  flex: 1;
}

.recipe-items {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.recipe-item {
  display: grid;
  grid-template-columns: 70px 1fr auto auto;
  gap: 10px;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 14px;
  align-items: center;
}

.recipe-item:hover {
  background: #f0f0f0;
}

.recipe-item-id {
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.recipe-item-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recipe-item-price {
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.recipe-items-empty {
  color: #6c757d;
  font-size: 13px;
  font-style: italic;
  margin-bottom: 8px;
}

.btn-icon {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
}

.btn-danger {
  background: transparent;
  border: 1px solid transparent;
  color: #6c757d;
}

.btn-danger:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #b91c1c;
}
</style>
